import { useParams, Link, useNavigate } from 'react-router-dom';
import { comics } from '@/mocks/comics';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import ComicCard from '@/components/base/ComicCard';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-300"></i>
            </div>
            <h2 className="text-xl font-semibold text-foreground-950 mb-2">漫画未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">该漫画不存在或已被移除</p>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i> 返回首页
            </button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryNames: Record<string, string> = {
    romance: '恋爱',
    fantasy: '奇幻',
    action: '动作',
    mystery: '悬疑',
    bl: 'BL',
    school: '校园',
    comedy: '搞笑',
    historical: '古风',
  };

  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const formatViews = (v: number) => {
    if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  const comicSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩国漫画网`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.chapters[comic.chapters.length - 1]?.date,
    dateModified: comic.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '韩国漫画网',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: '/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: `${categoryNames[comic.category] || comic.category}漫画`,
        item: `/category/${comic.category}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: comic.title,
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <section className="bg-background-100/50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <nav aria-label="面包屑导航" className="mb-8">
              <ol className="flex items-center gap-1.5 text-xs text-foreground-400 flex-wrap">
                <li>
                  <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line text-[10px]"></i>
                </li>
                <li>
                  <Link to={`/category/${comic.category}`} className="hover:text-primary-500 transition-colors">
                    {categoryNames[comic.category] || comic.category}漫画
                  </Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line text-[10px]"></i>
                </li>
                <li className="text-foreground-700 font-medium">{comic.title}</li>
              </ol>
            </nav>

            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-shrink-0 w-full md:w-[280px]">
                <div className="aspect-[3/4] rounded-xl overflow-hidden sticky top-24">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <span className="bg-primary-100 text-primary-700 text-xs px-2.5 py-1 rounded-full">
                    {categoryNames[comic.category] || comic.category}
                  </span>
                  <span className={`text-xs px-2.5 py-1 rounded-full ${
                    comic.status === '已完结'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                </div>

                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                  {comic.title}
                </h1>

                <p className="text-sm text-foreground-500 mb-6">作者：{comic.author}</p>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6 max-w-2xl">
                  {comic.description}
                </p>

                <div className="flex flex-wrap items-center gap-4 md:gap-6 mb-8">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100">
                      <i className="ri-star-fill text-accent-500"></i>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-foreground-950">{comic.rating}</div>
                      <div className="text-xs text-foreground-400">评分</div>
                    </div>
                  </div>
                  <div className="w-px h-10 bg-background-300/60 hidden sm:block"></div>
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className="ri-eye-line text-primary-500"></i>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-foreground-950">{formatViews(comic.views)}</div>
                      <div className="text-xs text-foreground-400">阅读量</div>
                    </div>
                  </div>
                  <div className="w-px h-10 bg-background-300/60 hidden sm:block"></div>
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100">
                      <i className="ri-file-list-3-line text-secondary-500"></i>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-foreground-950">{comic.chapters.length}+</div>
                      <div className="text-xs text-foreground-400">章节数</div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-full font-medium text-sm transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-play-circle-line"></i>
                    开始阅读 第{comic.chapters[0]?.num}话
                  </button>
                  <button className="inline-flex items-center gap-2 bg-background-100 hover:bg-background-200 border border-background-300/60 text-foreground-700 px-6 py-3 rounded-full font-medium text-sm transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-bookmark-line"></i>
                    收藏漫画
                  </button>
                  <button className="inline-flex items-center gap-2 bg-background-100 hover:bg-background-200 border border-background-300/60 text-foreground-700 px-6 py-3 rounded-full font-medium text-sm transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-share-forward-line"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
              章节列表
            </h2>

            <div className="border border-background-200/70 rounded-xl overflow-hidden">
              <div className="bg-background-100 px-4 md:px-6 py-3 border-b border-background-200/70">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground-700">共 {comic.chapters.length} 话</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground-400">排序：</span>
                    <select className="text-xs bg-background-50 border border-background-300/60 rounded-lg px-2 py-1 text-foreground-700 outline-none cursor-pointer">
                      <option>最新在前</option>
                      <option>最早在前</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="divide-y divide-background-200/50">
                {comic.chapters.map((chapter) => (
                  <div
                    key={chapter.num}
                    className="flex items-center justify-between px-4 md:px-6 py-3.5 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 text-xs font-bold">
                        {chapter.num}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground-950 truncate">
                          {chapter.title}
                        </p>
                        <p className="text-xs text-foreground-400">{chapter.date}</p>
                      </div>
                    </div>
                    <span className="flex-shrink-0 text-xs text-foreground-300 bg-background-100 hover:bg-primary-50 hover:text-primary-500 px-3 py-1.5 rounded-full transition-colors whitespace-nowrap">
                      免费阅读
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100/50">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                {relatedComics.map((rc) => (
                  <ComicCard
                    key={rc.id}
                    id={rc.id}
                    title={rc.title}
                    author={rc.author}
                    cover={rc.cover}
                    category={rc.category}
                    rating={rc.rating}
                    views={rc.views}
                    status={rc.status}
                    latestChapter={`第${rc.chapters[0]?.num}话`}
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(comicSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
    </>
  );
}